import { AlertTriangle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { SpecAdvisory } from './specAdvisories';

interface SpecAdvisoryBadgesProps {
  advisories: SpecAdvisory[];
  /** Apply the advisory's recommended fix to all its affected lines. */
  onApply: (advisory: SpecAdvisory) => void;
  disabled?: boolean;
  className?: string;
}

// Interactive warning capsules for spec-rule violations (86d3hk6pg). Each capsule shows the
// recommended correction + the count of affected lines; clicking applies the fix to all of
// them. Nothing renders when there are no violations. Shared by the SO and Quotation
// Line Items toolbars.
export function SpecAdvisoryBadges({
  advisories,
  onApply,
  disabled,
  className,
}: SpecAdvisoryBadgesProps) {
  if (advisories.length === 0) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
        {advisories.map((advisory) => {
          const actionLabel = `${advisory.description} Affects ${advisory.count} line${
            advisory.count === 1 ? '' : 's'
          }.`;
          return (
            <Tooltip key={advisory.key}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => onApply(advisory)}
                  disabled={disabled}
                  aria-label={actionLabel}
                  className={cn(
                    'inline-flex h-7 items-center gap-1.5 rounded-md border border-warning/40 bg-warning/10 px-2 text-xs font-medium text-warning',
                    'transition-colors hover:bg-warning/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    'disabled:pointer-events-none disabled:opacity-50',
                  )}
                >
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  <span>{advisory.label}</span>
                  <span className="tabular-nums opacity-80">· {advisory.count}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{actionLabel}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
